import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { CropField } from '@next/arable-lib/crop-field';
import { isLoaded, isLoading } from '@next/arable-lib/external-resource';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';

import { filter, map, Subject, take, takeUntil } from 'rxjs';

import { loadCustomerBlightRights } from 'src/app/store/actions';

import { SiteState } from '../../shared/models';
import { selectCropYear } from '../../store/selectors';
import { eLearningUrl, Insights } from '../shared/constants';
import { NotificationDto } from '../shared/models/models';
import {
  closeNotification,
  loadCropFields,
  loadNotifications,
  loadWaterAuthorityExceedances,
} from '../store/tga.actions';
import {
  selectCropFields,
  selectDismissedNotifications,
  selectLoadingWaterAuthorityExceedances,
  selectNotifications,
  selectSortedNotifications,
  selectWaterAuthorityExceedances,
} from '../store/tga.selectors';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NotificationsContainerComponent } from './notifications-container/notifications-container.component';
import { WeatherForecastWidgetComponent } from './weather-forecast-widget/weather-forecast-widget.component';
import { BlightDashboardWidgetComponent } from '../../blight-v2/components/dashboard-widget/blight-dashboard-widget.component';
import { BfpDashboardWidgetComponent } from '../../bem/dashboard-widget/bfp-dashboard-widget.component';
import { ListComponent } from '../../advices/components/dashboard-widget/list.component';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { WaterAuthorityExceedanceWidgetComponent } from './water-authority-exceedance-widget/water-authority-exceedance-widget.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@next/core-lib/i18n';
import { CropYearSelectorComponent } from '../../shared/components/crop-year-selector/crop-year-selector.component';

@Component({
    selector: 'app-tga',
    templateUrl: './tga.component.html',
    styleUrls: ['./tga.component.scss'],
    imports: [
        MatIconButton,
        MatIcon,
        NotificationsContainerComponent,
        WeatherForecastWidgetComponent,
        BlightDashboardWidgetComponent,
        BfpDashboardWidgetComponent,
        ListComponent,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        WaterAuthorityExceedanceWidgetComponent,
        AsyncPipe,
        TranslatePipe,
        CropYearSelectorComponent,
    ],
})
export class TgaComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  private readonly destroy$ = new Subject<void>();
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer), takeUntil(this.destroy$));
  private readonly notifications$ = this.store.pipe(select(selectSortedNotifications), takeUntil(this.destroy$));
  private readonly notificationsResource$ = this.store.pipe(select(selectNotifications), takeUntil(this.destroy$));
  private readonly dismissedNotifications$ = this.store.pipe(
    select(selectDismissedNotifications),
    takeUntil(this.destroy$),
  );

  notifications = signal<NotificationDto[]>([]);
  dismissedNotifications = signal<NotificationDto[]>([]);
  isNotificationsLoading = signal(false);

  readonly isLoaded = isLoaded;
  isMobile = false;
  waterAuthorityExceedances$ = this.store.select(selectWaterAuthorityExceedances);
  loadingWaterAuthorities$ = this.store.select(selectLoadingWaterAuthorityExceedances);

  waterAuthorityWidgetData$ = this.waterAuthorityExceedances$.pipe(
    map((results) =>
      (results ?? []).map((r) => ({
        title: r.waterBoardName,
        exceedances: (r.exceedances ?? []).map((e) => ({ substance: e.substance, example: e.cropAid })),
      })),
    ),
  );

  ngOnInit() {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'TGA.PageTitle' }));
    this.store.dispatch(loadCustomerBlightRights());

    // Subscribe to notifications
    this.notificationsResource$.subscribe((notificationsResource) => {
      this.isNotificationsLoading.set(isLoading(notificationsResource));
    });

    this.notifications$.subscribe((sortedNotifications) => {
      this.notifications.set(sortedNotifications);
    });

    this.dismissedNotifications$.subscribe((dismissed) => {
      this.dismissedNotifications.set(dismissed);
    });
    this.checkIsMobile();
    this.store.pipe(select(selectCropYear), take(1)).subscribe((cropYear) => {
      this.store.dispatch(loadNotifications({ cropYear }));

      // Ensure crop fields are requested if missing, and call loadWaterAuthorityExceedances
      this.store.pipe(select(selectCropFields), take(1)).subscribe((fields) => {
        if (!fields || fields.length === 0) {
          this.store.dispatch(loadCropFields({ cropYear }));
        } else {
          this.loadWaterAuthorityExceedances();
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  checkIsMobile(): void {
    this.breakpoint.ltMd$.pipe(take(1)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  private loadWaterAuthorityExceedances(): void {
    this.store
      .pipe(
        select(selectCropFields),
        take(1),
        map((tgaCropFields) =>
          Array.from(
            new Set(
              (tgaCropFields ?? [])
                .map((cf) => (cf.cropField as CropField & { waterAuthorityId?: number })?.waterAuthorityId)
                .filter((id): id is number => id != null),
            ),
          ),
        ),
        filter((uniqueIds) => uniqueIds.length > 0),
      )
      .subscribe((uniqueIds) => this.store.dispatch(loadWaterAuthorityExceedances(uniqueIds)));
  }

  openInfo() {
    this.ai.trackEvent(Insights.clickedWaterAuthority, {});
    window.open(eLearningUrl);
  }

  onNotificationDismissed(notification: NotificationDto): void {
    const notificationId = notification.id;
    if (notificationId) {
      this.store.dispatch(closeNotification({ notificationId }));
    }
  }
}
